import type { Deadspeak, EntityId, Resource } from '../types';
import { dmg, itemGrow, REAL_HOUR as HOUR } from '../model/game';
import { effects } from './effects';

// Meadows = 1
// Swamp = 2
// Mountain = 4
// BlackForest = 8
// Plains = 16
// Ashlands = 32
// DeepNorth = 64
// Ocean = 256
// Mistlands = 512

const effectsById = Object.fromEntries(effects.map(e => [e.id, e]));

function deadSpeakBoss(line: string): Deadspeak {
  return {
    interval: 60,
    chance: 0.05,
    triggerDistance: 10,
    ttl: 15,
    texts: [line],
  };
}

const disabled = true;
const floating = true;

export const resources: Resource[] = [
// MEADOWS
  { type: 'item', group: 'lumber', id: 'Wood', emoji: '🪵', tier: 0, weight: 2, stack: 50, floating,
    grow: itemGrow({ locations: ['Meadows', 'Swamp', 'BlackForest'], altitude: [0, 1000], inForest: [0, 1.1], num: [15, 15] })
  },
  { type: 'item', id: 'Coal', tier: 2, weight: 2, stack: 50 },
  { type: 'item', id: 'Resin', tier: 1, weight: 0.3, stack: 50 },
  { type: 'item', id: 'Feathers', emoji: '🪶', tier: 1, weight: 0.1, stack: 50 },
  { type: 'item', id: 'Stone', emoji: '🪨', tier: 0, weight: 2, stack: 50,
    grow: itemGrow(
      { locations: ['Meadows', 'Swamp'], altitude: [-3, 1000], num: [5, 5], },
      { locations: ['Meadows', 'Swamp', 'BlackForest', 'Plains'], altitude: [0, 1000], num: [30, 30], group: [2, 3], tilt: [22, 90] }
    ),
  },
  { type: 'item', id: 'Flint', tier: 1, weight: 2, stack: 30,
    grow: itemGrow({ locations: ['Meadows'], altitude: [-2, 1], num: [30, 30], respawn: 4 * HOUR }),
  },
  { type: 'item', id: 'StoneRock', tier: 1, weight: 2, stack: 30,
    grow: itemGrow({ locations: ['Meadows', 'BlackForest', 'Swamp', 'Mountain'], randTilt: 0, chanceToUseGroundTilt: 1, biomeArea: 6,
      altitude: [5, 20], groupRadius: 1, num: [0, 1] }), // minDistanceFromCenter: 1000
  },
  { type: 'item', group: 'hide', id: 'LeatherScraps', tier: 1, weight: 0.5, stack: 50 },
  { type: 'item', group: 'hide', id: 'DeerHide', tier: 1, weight: 1, stack: 50 },
  { type: 'item', id: 'QueenBee', emoji: '🐝', tier: 1, weight: 0.2, stack: 20 },
  { type: 'item', group: 'seedTree', id: 'BeechSeeds', tier: 1, weight: 0.1, stack: 100 },
  { type: 'item', group: 'berry', id: 'Raspberry', emoji: '🍓', tier: 1, weight: 0.1, stack: 50, tags: ['plant'],
    Food: { health: 7, stamina: 20, duration: 600, regen: 1 },
  },
  { type: 'item', id: 'Mushroom', emoji: '🍄🔴', tier: 1, weight: 0.1, stack: 50,
    Food: { health: 15, stamina: 15, duration: 900, regen: 1 },
    grow: itemGrow({
      locations: ['Meadows', 'BlackForest', 'Swamp' /* 11, also 'Swamp', but minAlt=1 */],
      altitude: [1, 1000], tilt: [0, 25], num: [1, 2], group: [3, 6], inForest: [0, 1], respawn: 4 * HOUR,
    }),
  },
  { type: 'item', id: 'Dandelion', tier: 0, weight: 0.1, stack: 50, tags: ['plant', 'herb'],
    grow: itemGrow({ locations: ['Meadows'], tilt: [0, 15], num: [8, 10], group: [1, 3], respawn: 4 * HOUR }), },
  { type: 'item', id: 'AxeHead1', tier: 1, weight: 2, stack: 10 },
  { type: 'item', id: 'AxeHead2', tier: 1, weight: 2, stack: 10 },
  { type: 'item', id: 'Honey', emoji: '🍯', tier: 1, weight: 0.2, stack: 50,
    Food: { health: 8, stamina: 35, duration: 900, regen: 1 },
    // recipe: { type: 'craft_one', time: GAME_DAY, materials: {}, source: { station: 'BeeHive' }, number: 1 },
  },
  { type: 'item', id: 'RawMeat', emoji: '🥩', tier: 0, weight: 1, stack: 20 },
  { type: 'item', id: 'CookedMeat', emoji: '🍗', tier: 1, weight: 1, stack: 20,
    Food: { health: 30, stamina: 10, duration: 1200, regen: 2 },
  },
  { type: 'item', id: 'BoarJerky', emoji: '🥓', tier: 1, weight: 0.5, stack: 20,
    Food: { health: 23, stamina: 23, duration: 1800, regen: 2 },
  },
  { type: 'item', id: 'DeerMeat', emoji: '🥩', tier: 1, weight: 1, stack: 20 },
  { type: 'item', id: 'CookedDeerMeat', emoji: '🍗', tier: 1, weight: 1, stack: 20,
    Food: { health: 35, stamina: 12, duration: 1200, regen: 2 },
  },
  { type: 'item', id: 'NeckTail', tier: 0, weight: 0.5, stack: 50 },
  { type: 'item', id: 'NeckTailGrilled', emoji: '🥓', tier: 1, weight: 0.5, stack: 20,
    Food: { health: 25, stamina: 8, duration: 1200, regen: 2 },
  },
  { type: 'item', id: 'FishAnglerRaw', emoji: '🐟', tier: 2, weight: 0.5, stack: 20, disabled },
  { type: 'item', id: 'FishRaw', emoji: '🐟', tier: 2, weight: 0.5, stack: 20 },
  { type: 'item', id: 'FishCooked', emoji: '🍣', tier: 2, weight: 0.5, stack: 20,
    Food: { health: 45, stamina: 15, duration: 1200, regen: 2 },
  },
  { type: 'item', id: 'FeastMeadows', emoji: '🥙', tier: 3, weight: 1, stack: 1,
    Food: { health: 35, stamina: 35, duration: 3000, regen: 2 },
  },
  { type: 'item', id: 'MeadBaseHealthMinor', emoji: '🍲', tier: 1, weight: 1, stack: 1 },
  { type: 'item', id: 'MeadHealthMinor', emoji: '\u{1F9EA}', tier: 1, weight: 1, stack: 10,
    Potion: { health: [50, 10], cooldown: 120 }, 
  },
  { type: 'item', id: 'MeadBaseStaminaMinor', emoji: '🍲', tier: 1, weight: 1, stack: 1 },
  { type: 'item', id: 'MeadStaminaMinor', emoji: '\u{1F9EA}', tier: 1, weight: 1, stack: 10,
    Potion: { stamina: [80, 2], cooldown: 120 }, 
  },
  { type: 'item', id: 'MeadBaseTasty', emoji: '🍲', tier: 1, weight: 1, stack: 1 },
  { type: 'item', id: 'MeadTasty', emoji: '\u{1F9EA}', tier: 1, weight: 1, stack: 10,
    Potion: { staminaRegen: 4, healthRegen: 0.5, cooldown: 120 }, 
  },
  { type: 'trophy', id: 'TrophyBoar', trophyPos: { x: 0, y: 0 }, emoji: '🐗', tier: 1, weight: 2, stack: 50 },
  { type: 'trophy', id: 'TrophyDeer', trophyPos: { x: 1, y: 0 }, emoji: '🦌', tier: 1, weight: 2, stack: 50, summon: ['Eikthyr', 2] },
  { type: 'trophy', id: 'TrophyNeck', trophyPos: { x: 2, y: 0 }, emoji: '🦎', tier: 1, weight: 0.5, stack: 50 },
  { type: 'trophy', id: 'TrophyEikthyr', trophyPos: { x: 3, y: 0 }, emoji: '🦌', tier: 2, weight: 2, stack: 20, floating, power: 'GP_Eikthyr',
    Deadspeak: deadSpeakBoss('ui.deadspeak.eikthyr'), },
  { type: 'item', id: 'HardAntler', tier: 2, weight: 2, stack: 50, floating },
// BLACK FOREST
  { type: 'item', group: 'seedTree', id: 'Acorn', tier: 2, weight: 0.1, stack: 100, floating },
  { type: 'item', group: 'seedTree', id: 'BirchSeeds', tier: 2, weight: 0.1, stack: 100, floating },
  { type: 'item', group: 'seedTree', id: 'FirCone', tier: 2, weight: 0.1, stack: 100, floating },
  { type: 'item', group: 'seedTree', id: 'PineCone', tier: 2, weight: 0.1, stack: 100, floating },
  { type: 'item', group: 'lumber', id: 'FineWood', tier: 2, weight: 2, stack: 50, floating },
  { type: 'item', id: 'GreydwarfEye', tier: 2, weight: 0.2, stack: 50 },
  { type: 'item', id: 'Pukeberries', tier: 2, weight: 0.1, stack: 50, effect: effectsById.Puke },
  { type: 'item', id: 'BoneFragments', emoji: '🦴', tier: 2, weight: 0.5, stack: 50 },
  { type: 'item', id: 'Ectoplasm', emoji: '🦠', tier: 2, weight: 2, stack: 50 },
  { type: 'item', id: 'SurtlingCore', emoji: '🟥', tier: 2, weight: 5, stack: 10,
    PointLight: { range: 1.5, intensity: 2, color: '#FF6A44' },
  },
  { type: 'item', id: 'AncientSeed', emoji: '🌰', tier: 2, weight: 1, stack: 50, floating, summon: ['gd_king', 3],
    PointLight: { range: 1.5, intensity: 2, color: '#C7FF00' },
  },
  { type: 'item', group: 'lumber', id: 'RoundLog', tier: 2, weight: 2, stack: 50, floating },
  { type: 'item', group: 'hide', id: 'TrollHide', tier: 2, weight: 2, stack: 20 },
  { type: 'item', group: 'hide', id: 'BjornHide', tier: 2, weight: 1, stack: 50 },
  { type: 'item', id: 'BjornPaw', tier: 2, weight: 0.5, stack: 50 },
  { type: 'item', group: 'ore', id: 'CopperOre', tier: 2, weight: 10, stack: 30, teleportable: false },
  { type: 'item', group: 'ore', id: 'CopperScrap', tier: 6, weight: 10, stack: 30, teleportable: false },
  { type: 'item', group: 'ore', id: 'BronzeScrap', tier: 6, disabled, weight: 10, stack: 30, teleportable: false },
  { type: 'item', group: 'metal', id: 'Copper', tier: 2, weight: 10, stack: 30, teleportable: false, tags: ['metal'] },
  { type: 'item', group: 'ore', id: 'TinOre', tier: 2, weight: 8, stack: 30, teleportable: false },
  { type: 'item', group: 'metal', id: 'Tin', tier: 2, weight: 8, stack: 30, teleportable: false, tags: ['metal'] },
  { type: 'item', group: 'metal', id: 'Bronze', tier: 2, weight: 12, stack: 30, teleportable: false, tags: ['metal'] },
  { type: 'item', id: 'BronzeNails', tier: 2, weight: 0.5, stack: 100 },
  { type: 'item', group: 'value', id: 'Coins', emoji: '🪙', tier: 2, weight: 0.1, stack: 999, Value: 1 },
  { type: 'item', group: 'value', id: 'Ruby', emoji: '♦️', tier: 2, weight: 0.1, stack: 20, Value: 20 },
  { type: 'item', group: 'value', id: 'Amber', emoji: '🍕', tier: 2, weight: 0.1, stack: 20, Value: 5 },
  { type: 'item', group: 'value', id: 'AmberPearl', emoji: '🟡', tier: 2, weight: 0.1, stack: 50, Value: 10 },
  { type: 'item', group: 'value', id: 'SilverNecklace', emoji: '💍', tier: 2, weight: 0.1, stack: 50, Value: 30 },
  { type: 'item', id: 'YmirRemains', tier: 2, weight: 0.3, stack: 50 },
  { type: 'item', id: 'FishingBait', tier: 2, weight: 0.1, stack: 100, floating },
  { type: 'item', id: 'Thunderstone', tier: 2, weight: 10, stack: 20,
    PointLight: { color: '#3AC8FF', range: 2, intensity: 1 },
  },
  { type: 'item', group: 'berry', id: 'Blueberries', emoji: '🫐', tier: 2, weight: 0.1, stack: 50, tags: ['plant'],
    Food: { health: 8, stamina: 25, duration: 600, regen: 1 },
  },
  { type: 'item', id: 'QueensJam', emoji: '🧁', tier: 2, weight: 1, stack: 10,
    Food: { health: 14, stamina: 40, duration: 1200, regen: 2 },
  },
  { type: 'item', id: 'MushroomYellow', emoji: '🍄🟡', tier: 2, weight: 0.1, stack: 50,
    Food: { health: 10, stamina: 30, duration: 600, regen: 1 },
    PointLight: { range: 1.5, intensity: 1.5, color: '#FFE36D' },
  },
  { type: 'item', id: 'MushroomBlue', emoji: '🍄🔵', tier: 2, weight: 0.1, stack: 50,
    disabled,
    Food: { health: 20, stamina: 20, duration: 600, regen: 1 },
    PointLight: { range: 1.5, intensity: 1.5, color: '#9FDBFF' },
  },
  { type: 'item', id: 'GlowingMushroom', emoji: '🍄⚪', tier: 2, weight: 0.1, stack: 50,
    disabled,
    PointLight: { range: 10, intensity: 2, color: '#658290' }
  },
  { type: 'item', id: 'BjornMeat', emoji: '🥩', tier: 2, weight: 1, stack: 20 },
  { type: 'item', id: 'CookedBjornMeat', emoji: '🍖', tier: 2, weight: 1, stack: 20,
    Food: { health: 40, stamina: 13, duration: 1200, regen: 2 },
  },
  { type: 'item', id: 'BarrelRings', tier: 3, weight: 2, stack: 20 },
  { type: 'item', id: 'CandleWick', tier: 3, weight: 0.3, stack: 50 },
  { type: 'item', id: 'ScytheHandle', tier: 3, weight: 2, stack: 20 },
  { type: 'item', id: 'MeadTrollPheromones', tier: 3, emoji: '\u{1F9EA}', weight: 1, stack: 10,
    effect: effectsById.Potion_TrollPheromones,
  },
  { type: 'item', id: 'MeadBaseHasty', emoji: '🍲', tier: 3, weight: 1, stack: 1 },
  { type: 'item', id: 'MeadHasty', emoji: '\u{1F9EA}', tier: 3, weight: 1, stack: 10,
    effect: effectsById.Potion_hasty,
  },
  { type: 'item', id: 'MeadBaseBzerker', emoji: '🍲', tier: 3, weight: 1, stack: 1 },
  { type: 'item', id: 'MeadBzerker', emoji: '\u{1F9EA}', tier: 3, weight: 1, stack: 10,
    effect: effectsById.Potion_Bzerker,
  },
  { type: 'item', id: 'MeadBaseStrength', emoji: '🍲', tier: 3, weight: 1, stack: 1 },
  { type: 'item', id: 'MeadStrength', emoji: '\u{1F9EA}', tier: 3, weight: 1, stack: 10,
    effect: effectsById.Potion_strength,
  },
  { type: 'item', id: 'MeadBaseLightFoot', emoji: '🍲', tier: 6, weight: 1, stack: 1 },
  { type: 'item', id: 'MeadLightfoot', emoji: '\u{1F9EA}', tier: 6, weight: 1, stack: 10,
    effect: effectsById.Potion_LightFoot,
  },
  { type: 'item', id: 'MeadBaseSwimmer', emoji: '🍲', tier: 3, weight: 1, stack: 1 },
  { type: 'item', id: 'MeadSwimmer', emoji: '\u{1F9EA}', tier: 3, weight: 1, stack: 10,
    effect: effectsById.Potion_swimmer,
  },
  { type: 'item', id: 'MeadBaseTamer', emoji: '🍲', tier: 4, weight: 1, stack: 1 },
  { type: 'item', id: 'MeadTamer', emoji: '\u{1F9EA}', tier: 4, weight: 1, stack: 10,
    effect: effectsById.Potion_tamer,
  },
  { type: 'item', id: 'MeadBaseBugRepellent', emoji: '🍲', tier: 5, weight: 1, stack: 1 },
  { type: 'item', id: 'MeadBugRepellent', emoji: '\u{1F9EA}', tier: 5, weight: 1, stack: 10,
    effect: effectsById.Potion_BugRepellent,
  },

  { type: 'item', id: 'MushroomBzerker', tier: 3, weight: 0.1, stack: 50 },
  { type: 'item', id: 'FragrantBundle', tier: 3, weight: 0.5, stack: 50 },
  { type: 'item', id: 'FreshSeaweed', tier: 3, weight: 0.5, stack: 50 },
  { type: 'item', id: 'CuredSquirrelHamstring', tier: 4, weight: 0.5, stack: 50 },
  { type: 'item', id: 'PowderedDragonEgg', tier: 4, weight: 0.5, stack: 50 },
  { type: 'item', id: 'PungentPebbles', tier: 6, weight: 0.5, stack: 50 },
  { type: 'item', id: 'VineGreenSeeds', tier: 3, weight: 0.1, stack: 100 },
  { type: 'item', id: 'SpiceForests', tier: 2, weight: 0.1, stack: 100 },
  { type: 'item', id: 'SpiceOceans', tier: 3, weight: 0.1, stack: 100 },
  { type: 'item', id: 'SpiceMountains', tier: 4, weight: 0.1, stack: 100 },
  { type: 'item', id: 'SpicePlains', tier: 5, weight: 0.1, stack: 100 },
  { type: 'item', id: 'SpiceMistlands', tier: 6, weight: 0.1, stack: 100 },
  { type: 'item', id: 'SpiceAshlands', tier: 7, weight: 0.1, stack: 100 },
  { type: 'item', id: 'MeadBaseHealthMedium', emoji: '🍲', tier: 3, weight: 1, stack: 1 },
  { type: 'item', id: 'MeadHealthMedium', emoji: '\u{1F9EA}', tier: 3, weight: 1, stack: 10,
    Potion: { health: [75, 10], cooldown: 120 },
  },
  { type: 'item', id: 'MeadBasePoisonResist', emoji: '🍲', tier: 2, weight: 1, stack: 1 },
  { type: 'item', id: 'MeadPoisonResist', emoji: '\u{1F9EA}', tier: 2, weight: 1, stack: 10,
    Potion: { damageModifiers: { poison: 'veryResistant' }, cooldown: 600 },
  },
  { type: 'item', id: 'Thistle', emoji: '🌿', tier: 2, weight: 0.1, stack: 50, tags: ['plant', 'herb'],
    grow: itemGrow({ locations: ['BlackForest', 'Swamp'], altitude: [0, 1000], tilt: [0, 20], num: [1, 2], group: [2, 5], respawn: 4 * HOUR }),
  },
  { type: 'item', group: 'seedVeg', id: 'CarrotSeeds', tier: 2, weight: 0.1, stack: 100, tags: ['plant', 'vegetable'],
    grow: itemGrow({ locations: ['BlackForest'], num: [0, 0.5], group: [1, 2], tilt: [0, 25] }),
  },
  { type: 'item', id: 'Carrot', emoji: '🥕', tier: 2, weight: 0.3, stack: 50, tags: ['plant', 'vegetable'],
    Food: { health: 10, stamina: 32, duration: 900, regen: 1 },
  },
  { type: 'item', id: 'CarrotSoup', emoji: '🥣', tier: 2, weight: 1, stack: 10,
    Food: { health: 15, stamina: 45, duration: 1500, regen: 2 },
  },
  { type: 'item', id: 'DeerStew', emoji: '🍲', tier: 2, weight: 1, stack: 10,
    Food: { health: 45, stamina: 15, duration: 1500, regen: 3 },
  },
  { type: 'item', id: 'MinceMeatSauce', emoji: '', tier: 2, weight: 1, stack: 10,
    Food: { health: 40, stamina: 13, duration: 1500, regen: 3 },
  },
  { type: 'item', id: 'FeastBlackforest', emoji: '🥙', tier: 3, weight: 1, stack: 1,
    Food: { health: 35, stamina: 35, duration: 3000, regen: 3 },
  },
  { type: 'trophy', id: 'TrophySkeleton', trophyPos: { x: 5, y: 1 }, emoji: '💀', tier: 2, weight: 0.5, stack: 20 },
  { type: 'trophy', id: 'TrophySkeletonPoison', trophyPos: { x: 6, y: 1 }, emoji: '☠️', tier: 2, weight: 0.5, stack: 20 },
  { type: 'trophy', id: 'TrophySkeletonHildir', trophyPos: { x: 2, y: 6 }, emoji: '💀', tier: 2, weight: 0.5, stack: 20 },
  { type: 'trophy', id: 'TrophyGhost', trophyPos: { x: 3, y: 8 }, emoji: '👻', tier: 2, weight: 2, stack: 20 },
  { type: 'trophy', id: 'TrophyGreydwarf', trophyPos: { x: 0, y: 1 }, tier: 2, weight: 1, stack: 20 },
  { type: 'trophy', id: 'TrophyGreydwarfBrute', trophyPos: { x: 1, y: 1 }, tier: 2, weight: 1.5, stack: 20 },
  { type: 'trophy', id: 'TrophyGreydwarfShaman', trophyPos: { x: 2, y: 1 }, tier: 2, weight: 1, stack: 20 },
  { type: 'trophy', id: 'TrophyFrostTroll', trophyPos: { x: 4, y: 1 }, tier: 2, weight: 4, stack: 20 },
  { type: 'trophy', id: 'TrophyForestTroll', trophyPos: { x: 5, y: 1 }, tier: 2, weight: 4, stack: 20, disabled },
  { type: 'trophy', id: 'TrophyBjorn', trophyPos: { x: 4, y: 8 }, tier: 2, weight: 2, stack: 20 },
  { type: 'trophy', id: 'TrophyTheElder', trophyPos: { x: 3, y: 1 }, tier: 3, weight: 4, stack: 20, floating, power: 'GP_TheElder',
    Deadspeak: deadSpeakBoss('ui.deadspeak.elder'), },
  { type: 'item', id: 'CryptKey', emoji: '🗝️', tier: 3, weight: 0.1, stack: 1, floating,
    PointLight: { range: 3, intensity: 1, color: '#39FFFF' },
  },
  // SWAMP
  { type: 'item', id: 'Ooze', tier: 3, weight: 0.5, stack: 50 },
  { type: 'item', id: 'Guck', tier: 3, weight: 0.5, stack: 50 },
  { type: 'item', id: 'Root', tier: 3, weight: 0.3, stack: 50, floating },
  { type: 'item', id: 'Chain', emoji: '⛓️', tier: 3, weight: 2, stack: 50 },
  { type: 'item', id: 'Bloodbag', emoji: '🩸', tier: 3, weight: 0.5, stack: 50 },
  { type: 'item', id: 'Turnip', tier: 3, emoji: '🍆', weight: 0.3, stack: 50, tags: ['plant', 'vegetable'] },
  { type: 'item', group: 'seedVeg', id: 'TurnipSeeds', tier: 3, weight: 0.1, stack: 100, tags: ['plant', 'vegetable'],
    grow: itemGrow({ locations: ['Swamp'], altitude: [0, 1000], num: [0, 0.5], group: [1, 2], tilt: [0, 25] }) },
  { type: 'item', id: 'TurnipStew', emoji: '🍲', tier: 3, weight: 1, stack: 10,
    Food: { health: 18, stamina: 55, duration: 1500, regen: 2 },
  },
  { type: 'item', id: 'BlackSoup', emoji: '', tier: 3, weight: 1, stack: 10,
    Food: { health: 50, stamina: 17, duration: 1200, regen: 3 },
  },
  { type: 'item', id: 'ShocklateSmoothie', emoji: '', tier: 3, weight: 1, stack: 10,
    Food: { health: 16, stamina: 50, duration: 1200, regen: 1 },
  },
  { type: 'item', id: 'Entrails', tier: 3, weight: 0.5, stack: 50 },
  { type: 'item', id: 'Sausages', emoji: '🌭', tier: 3, weight: 0.5, stack: 20,
    Food: { health: 55, stamina: 18, duration: 1500, regen: 3 },
  },
  { type: 'item', id: 'FeastSwamps', emoji: '🥙', tier: 3, weight: 1, stack: 1,
    Food: { health: 35, stamina: 35, duration: 3000, regen: 3 },
  },
  { type: 'item', id: 'MeadBaseFrostResist', emoji: '🍲', tier: 3, weight: 1, stack: 1 },
  { type: 'item', id: 'MeadFrostResist', emoji: '\u{1F9EA}', tier: 3, weight: 1, stack: 10,
    Potion: { damageModifiers: { frost: 'resistant' }, cooldown: 600 },
  },
  { type: 'item', group: 'lumber', id: 'ElderBark', tier: 3, weight: 2, stack: 50, floating },
  { type: 'item', group: 'ore', id: 'IronScrap', tier: 3, weight: 10, stack: 30, teleportable: false },
  { type: 'item', group: 'ore', id: 'IronOre', tier: 3, weight: 10, stack: 30, teleportable: false },
  { type: 'item', group: 'metal', id: 'Iron', tier: 3, weight: 12, stack: 30, teleportable: false, tags: ['metal'] },
  { type: 'item', id: 'IronNails', tier: 3, weight: 0.5, stack: 100 },
  { type: 'item', id: 'SharpeningStone', tier: 3, weight: 6, stack: 20 },
  { type: 'item', id: 'WitheredBone', emoji: '🦴', tier: 3, weight: 1, stack: 30, floating, summon: ['Bonemass', 10] },
  { type: 'item', id: 'BlobVial', emoji: '🍼', tier: 3, weight: 1, stack: 10 },
  { type: 'trophy', id: 'TrophyLeech', trophyPos: { x: 0, y: 2 }, emoji: '🧛', tier: 3, weight: 2, stack: 20 },
  { type: 'trophy', id: 'TrophyBlob', trophyPos: { x: 6, y: 2 }, tier: 3, weight: 2, stack: 20 },
  { type: 'trophy', id: 'TrophySurtling', trophyPos: { x: 0, y: 6 }, tier: 3, weight: 1, stack: 20,
    PointLight: { range: 1, intensity: 3, color: '#FFB174' },
    /* warmRadius: 2, */ }, 
  { type: 'trophy', id: 'TrophyDraugr', trophyPos: { x: 1, y: 2 }, emoji: '🧟', tier: 3, weight: 2, stack: 20 },
  { type: 'trophy', id: 'TrophyDraugrElite', trophyPos: { x: 2, y: 2 }, emoji: '🧟', tier: 3, weight: 2, stack: 20 },
  { type: 'trophy', id: 'TrophyWraith', trophyPos: { x: 4, y: 2 }, emoji: '👻', tier: 3, weight: 1, stack: 20 },
  { type: 'trophy', id: 'TrophyAbomination', trophyPos: { x: 5, y: 2 }, emoji: '🥦', tier: 3, weight: 4, stack: 20, floating },
  { type: 'trophy', id: 'TrophyKvastur', trophyPos: { x: 0, y: 8 }, emoji: '', tier: 3, weight: 2, stack: 20, floating },
  { type: 'trophy', id: 'TrophyBonemass', trophyPos: { x: 3, y: 2 }, emoji: '🦠', tier: 4, weight: 2, stack: 20, floating, power: 'GP_Bonemass',
    Deadspeak: deadSpeakBoss('ui.deadspeak.bonemass'), },
// OCEAN
  { type: 'item', id: 'Chitin', tier: 2, weight: 2, stack: 50 },
  { type: 'item', id: 'SerpentScale', tier: 3, weight: 2, stack: 50 },
  { type: 'item', id: 'SerpentMeat', tier: 3, weight: 10, stack: 50, floating },
  { type: 'item', id: 'SerpentMeatCooked', emoji: '🍣', tier: 3, weight: 10, stack: 50,
    Food: { health: 70, stamina: 23, duration: 1500, regen: 3 },
  },
  { type: 'item', id: 'SerpentStew', emoji: '🍜', tier: 3, weight: 1, stack: 10,
    Food: { health: 80, stamina: 26, duration: 1800, regen: 4 },
  },
  { type: 'item', id: 'FeastOceans', emoji: '🥙', tier: 3, weight: 1, stack: 1,
    Food: { health: 45, stamina: 45, duration: 3000, regen: 3 },
  },
  { type: 'trophy', id: 'TrophySerpent', trophyPos: { x: 1, y: 6 }, tier: 3, weight: 25, stack: 20 },
// MOUNTAIN
  { type: 'item', group: 'hide', id: 'WolfPelt', tier: 4, weight: 1, stack: 50 },
  { type: 'item', id: 'WolfFang', tier: 4, weight: 0.1, stack: 50 },
  { type: 'item', id: 'WolfMeat', tier: 4, weight: 1, stack: 20 },
  { type: 'item', id: 'RottenMeat', tier: 4, weight: 0.5, stack: 20, effect: effectsById.Puke },
  { type: 'item', id: 'Onion', emoji: '', tier: 4, weight: 0.3, stack: 50,
    Food: { health: 13, stamina: 40, duration: 900, regen: 1 },
  },
  { type: 'item', id: 'WolfClaw', emoji: '', tier: 4, weight: 0.5, stack: 20 },
  { type: 'item', id: 'WolfHairBundle', emoji: '', tier: 4, weight: 1, stack: 50 },
  { type: 'item', id: 'JuteRed', emoji: '', tier: 4, weight: 2, stack: 50 },
  { type: 'item', group: 'seedVeg', id: 'OnionSeeds', emoji: '', tier: 4, weight: 0.1, stack: 100 },
  { type: 'item', id: 'FreezeGland', tier: 4, weight: 0.5, stack: 50,
    PointLight: { color: '#4FF0FF', range: 1, intensity: 2.56 },
  },
  { type: 'item', group: 'ore', id: 'SilverOre', tier: 4, weight: 14, stack: 30, teleportable: false },
  { type: 'item', group: 'metal', id: 'Silver', tier: 4, weight: 14, stack: 30, teleportable: false, tags: ['metal'] },
  { type: 'item', id: 'Obsidian', tier: 4, weight: 2, stack: 50 },
  { type: 'item', id: 'Crystal', emoji: '💎', tier: 4, weight: 1, stack: 50,
    PointLight: { color: '#8CE9FF', range: 3, intensity: 1 },
  },
  { type: 'item', id: 'DragonEgg', emoji: '🥚', tier: 4, weight: 200, stack: 1,
    floating, teleportable: false, summon: ['Dragon', 3], // respawn: 8 * HOUR
    PointLight: { color: '#FF81F6', range: 3, intensity: 2 },
  },
  { type: 'trophy', id: 'TrophyWolf', trophyPos: { x: 0, y: 3 }, tier: 4, weight: 1.5, stack: 20 },
  { type: 'trophy', id: 'TrophyFenring', trophyPos: { x: 1, y: 3 }, tier: 4, weight: 2, stack: 20 },
  { type: 'trophy', id: 'TrophyUlv', trophyPos: { x: 5, y: 3 }, tier: 4, weight: 1.5, stack: 20 },
  { type: 'trophy', id: 'TrophyCultist', trophyPos: { x: 6, y: 3 }, tier: 4, weight: 3, stack: 20 },
  { type: 'trophy', id: 'TrophyCultist_Hildir', trophyPos: { x: 4, y: 6 }, tier: 4, weight: 3, stack: 20 },
  { type: 'trophy', id: 'TrophyHatchling', trophyPos: { x: 4, y: 3 }, tier: 4, weight: 2, stack: 20 },
  { type: 'trophy', id: 'TrophySGolem', trophyPos: { x: 2, y: 3 }, tier: 4, weight: 2, stack: 20,
    PointLight: { color: '#81D5FF', range: 4, intensity: 1.5 },
  },
  { type: 'trophy', id: 'TrophyDragonQueen', trophyPos: { x: 3, y: 3 }, tier: 5, weight: 2, stack: 20, floating, power: 'GP_Moder',
    Deadspeak: deadSpeakBoss('ui.deadspeak.moder'), },
  { type: 'item', id: 'CookedWolfMeat', emoji: '', tier: 4, weight: 1, stack: 20,
    Food: { health: 45, stamina: 15, duration: 1200, regen: 3 },
  },
  { type: 'item', id: 'WolfMeatSkewer', emoji: '🍢', tier: 4, weight: 0.5, stack: 20,
    Food: { health: 65, stamina: 21, duration: 1500, regen: 3 },
  },
  { type: 'item', id: 'WolfJerky', emoji: '🥓', tier: 4, weight: 0.5, stack: 20,
    Food: { health: 33, stamina: 33, duration: 1800, regen: 3 },
  },
  { type: 'item', id: 'Eyescream', emoji: '🍦', tier: 4, weight: 0.5, stack: 10,
    Food: { health: 21, stamina: 65, duration: 1500, regen: 1 },
  },
  { type: 'item', id: 'OnionSoup', emoji: '🍲', tier: 4, weight: 1, stack: 10,
    Food: { health: 20, stamina: 60, duration: 1200, regen: 1 },
  },
  { type: 'item', id: 'FeastMountains', emoji: '🥙', tier: 4, weight: 1, stack: 1,
    Food: { health: 45, stamina: 45, duration: 3000, regen: 3 },
  },
  { type: 'item', id: 'DragonTear', tier: 5, weight: 1, stack: 50, floating,
    PointLight: { color: '#87DCFF', range: 3, intensity: 1.5 },
  },
// PLAINS
  { type: 'item', id: 'Flax', tier: 5, weight: 0.2, stack: 100, tags: ['plant', 'crop'],
/*    recipe: { type: 'craft_one', time: 4500, materials: { Flax: 1 },
              source: { station: CraftingStation.Cultivator }, number: 2 },*/
  },
  { type: 'item', id: 'LinenThread', tier: 5, weight: 2, stack: 50 },
  { type: 'item', id: 'Barley', emoji: '🌾', tier: 5, weight: 0.2, stack: 100, tags: ['plant', 'crop'],
/*    recipe: { type: 'craft_one', time: 4500, materials: { Barley: 1 },
              source: { station: CraftingStation.Cultivator }, number: 2 },*/
  },
  { type: 'item', id: 'BarleyFlour', tier: 5, weight: 0.2, stack: 50 },
  { type: 'item', group: 'berry', id: 'Cloudberry', emoji: '🟠', tier: 5, weight: 0.1, stack: 50, tags: ['plant'],
    Food: { health: 13, stamina: 40, duration: 900, regen: 1 },
  },
  { type: 'item', id: 'LoxMeat', tier: 5, weight: 2, stack: 20 },
  { type: 'item', group: 'hide', id: 'LoxPelt', tier: 5, weight: 1, stack: 50 },
  { type: 'item', id: 'UndeadBjornRibcage', emoji: '🦴', tier: 5, weight: 5, stack: 50 },
  { type: 'item', id: 'CookedLoxMeat', emoji: '🍖', tier: 5, weight: 2, stack: 20,
    Food: { health: 50, stamina: 16, duration: 1200, regen: 4 },
  },
  { type: 'item', id: 'BloodPudding', emoji: '🧆', tier: 5, weight: 1, stack: 10,
    Food: { health: 25, stamina: 75, duration: 1800, regen: 2 },
  },
  { type: 'item', id: 'BreadDough', emoji: '', tier: 5, weight: 0.5, stack: 20 },
  { type: 'item', id: 'Bread', emoji: '🍞', tier: 5, weight: 0.5, stack: 20,
    Food: { health: 23, stamina: 70, duration: 1500, regen: 2 },
  },
  { type: 'item', id: 'FishWraps', emoji: '🌯', tier: 5, weight: 1, stack: 10,
    Food: { health: 70, stamina: 23, duration: 1500, regen: 4 },
  },
  { type: 'item', id: 'LoxPieUncooked', emoji: '', tier: 5, weight: 1, stack: 10 },
  { type: 'item', id: 'LoxPie', emoji: '🥧', tier: 5, weight: 1, stack: 10,
    Food: { health: 75, stamina: 24, duration: 1800, regen: 4 },
  },
  { type: 'item', id: 'VikingCupcakeUncooked', emoji: '', tier: 5, weight: 1, stack: 10 },
  { type: 'item', id: 'VikingCupcake', emoji: '🧁', tier: 5, weight: 1, stack: 10,
    Food: { health: 43, stamina: 43, duration: 1800, regen: 4 },
  },
  { type: 'item', id: 'FeastPlains', emoji: '🥙', tier: 5, weight: 1, stack: 1,
    Food: { health: 55, stamina: 55, duration: 3000, regen: 4 },
  },
  { type: 'item', id: 'MeadBaseStaminaMedium', emoji: '🍲', tier: 5, weight: 1, stack: 1 },
  { type: 'item', id: 'MeadStaminaMedium', emoji: '\u{1F9EA}', tier: 5, weight: 1, stack: 10,
    Potion: { stamina: [160, 2], cooldown: 120 },
  },
  { type: 'item', id: 'MeadBaseBarleyWine', emoji: '🍲', tier: 5, weight: 1, stack: 1 },
  { type: 'item', id: 'BarleyWine', emoji: '\u{1F9EA}', tier: 5, weight: 1, stack: 10,
    Potion: { damageModifiers: { fire: 'resistant' }, cooldown: 600 },
  },
  { type: 'item', id: 'Tar', emoji: '⚫', tier: 5, weight: 2, stack: 50, floating }, // Pickable
  { type: 'item', id: 'Needle', emoji: '🪡', tier: 5, weight: 0.5, stack: 50 },
  { type: 'item', group: 'ore', id: 'BlackMetalScrap', tier: 5, weight: 10, stack: 30, teleportable: false },
  { type: 'item', group: 'metal', id: 'BlackMetal', tier: 5, weight: 12, stack: 30, teleportable: false, tags: ['metal'] },
  { type: 'item', id: 'SaddleLox', tier: 5, weight: 10, stack: 1, floating },
  { type: 'item', id: 'GoblinTotem', tier: 5, weight: 1, stack: 30, summon: ['GoblinKing', 5],
    PointLight: { color: '#FF44AC', range: 2, intensity: 1.4 },
  },
  { type: 'trophy', id: 'TrophyLox', trophyPos: { x: 4, y: 4 }, tier: 5, weight: 2, stack: 20 },
  { type: 'trophy', id: 'TrophyBjornUndead', trophyPos: { x: 5, y: 8 }, tier: 5, weight: 2, stack: 20 },
  { type: 'trophy', id: 'TrophyDeathsquito', trophyPos: { x: 5, y: 4 }, tier: 5, weight: 0.5, stack: 20 },
  { type: 'trophy', id: 'TrophyGrowth', trophyPos: { x: 6, y: 4 }, tier: 5, weight: 1, stack: 20 },
  { type: 'trophy', id: 'TrophyGoblin', trophyPos: { x: 0, y: 4 }, tier: 5, weight: 2, stack: 20 },
  { type: 'trophy', id: 'TrophyGoblinBrute', trophyPos: { x: 1, y: 4 }, tier: 5, weight: 2, stack: 20 },
  { type: 'trophy', id: 'TrophyGoblinBruteBrosBrute', trophyPos: { x: 5, y: 6 }, tier: 5, weight: 2, stack: 20 },
  { type: 'trophy', id: 'TrophyGoblinShaman', trophyPos: { x: 2, y: 4 }, tier: 5, weight: 0.5, stack: 20 },
  { type: 'trophy', id: 'TrophyGoblinBruteBrosShaman', trophyPos: { x: 6, y: 6 }, tier: 5, weight: 2, stack: 20 },
  { type: 'trophy', id: 'TrophyGoblinKing', trophyPos: { x: 3, y: 4 }, tier: 5, weight: 1, stack: 20, floating, power: 'GP_Yagluth',
    Deadspeak: deadSpeakBoss('ui.deadspeak.yagluth'), },
  { type: 'item', id: 'YagluthDrop', tier: 5, weight: 1, stack: 30, floating,
    PointLight: { color: '#CB00FF', range: 3.76, intensity: 1.5 },
  },
// MISTLANDS
  { type: 'item', id: 'FishingBaitForest', tier: 2, weight: 0.1, stack: 100, floating },
  { type: 'item', id: 'FishingBaitOcean', tier: 2, weight: 0.1, stack: 100, floating },
  { type: 'item', id: 'FishingBaitCave', tier: 4, weight: 0.1, stack: 100, floating },
  { type: 'item', id: 'FishingBaitSwamp', tier: 3, weight: 0.1, stack: 100, floating },
  { type: 'item', id: 'FishingBaitPlains', tier: 5, weight: 0.1, stack: 100, floating },
  { type: 'item', id: 'FishingBaitMistlands', tier: 6, weight: 0.1, stack: 100, floating },
  { type: 'item', id: 'FishingBaitAshlands', tier: 7, weight: 0.1, stack: 100, floating },
  { type: 'item', id: 'FishingBaitDeepNorth', tier: 7, weight: 0.1, stack: 100, floating },
  { type: 'item', id: 'ChickenEgg', emoji: '🥚', tier: 6, weight: 1, stack: 20,
    EggGrow: { growTime: 1800, grownId: 'Chicken', requiresNearbyFire: true, requiresUnderRoof: true },
  },
  { type: 'item', id: 'HareMeat', emoji: '🍗', tier: 6, weight: 1, stack: 20 },
  { type: 'item', id: 'ScaleHide', emoji: '', tier: 6, weight: 0.5, stack: 50 },
  { type: 'item', id: 'CookedEgg', emoji: '🍳', tier: 6, weight: 1, stack: 50,
    Food: { health: 35, stamina: 12, duration: 1200, regen: 2 } },
  { type: 'item', id: 'MushroomJotunPuffs', emoji: '🍄', tier: 6, weight: 0.1, stack: 50,
    Food: { health: 25, stamina: 25, duration: 900, regen: 1 } },
  { type: 'item', id: 'MushroomMagecap', emoji: '🍄', tier: 6, weight: 0.1, stack: 50,
    Food: { health: 25, stamina: 25, eitr: 25, duration: 900, regen: 1 } },
  { type: 'item', id: 'CookedHareMeat', emoji: '🍗', tier: 6, weight: 1, stack: 20,
    Food: { health: 60, stamina: 20, duration: 1200, regen: 5 } },
  { type: 'item', id: 'ChickenMeat', emoji: '🍗', tier: 6, weight: 1, stack: 20 },
  { type: 'item', id: 'CookedChickenMeat', emoji: '🍗', tier: 6, weight: 1, stack: 20,
    Food: { health: 60, stamina: 20, duration: 1200, regen: 5 } },
  { type: 'item', id: 'FishAndBreadUncooked', emoji: '', tier: 6, weight: 1, stack: 10 },
  { type: 'item', id: 'FishAndBread', emoji: '', tier: 6, weight: 1, stack: 10,
    Food: { health: 30, stamina: 90, duration: 1800, regen: 3 } },
  { type: 'item', id: 'MeatPlatterUncooked', emoji: '', tier: 6, weight: 1, stack: 10 },
  { type: 'item', id: 'MeatPlatter', emoji: '', tier: 6, weight: 1, stack: 10,
    Food: { health: 80, stamina: 26, duration: 1800, regen: 5 } },
  { type: 'item', id: 'HoneyGlazedChickenUncooked', emoji: '', tier: 6, weight: 1, stack: 10 },
  { type: 'item', id: 'HoneyGlazedChicken', emoji: '', tier: 6, weight: 1, stack: 10,
    Food: { health: 80, stamina: 26, duration: 1800, regen: 5 } },
  { type: 'item', id: 'MisthareSupremeUncooked', emoji: '', tier: 6, weight: 1, stack: 10 },
  { type: 'item', id: 'MisthareSupreme', emoji: '', tier: 6, weight: 1, stack: 10,
    Food: { health: 85, stamina: 28, duration: 1500, regen: 5 } },
  { type: 'item', id: 'MagicallyStuffedShroomUncooked', emoji: '', tier: 6, weight: 1, stack: 10 },
  { type: 'item', id: 'MagicallyStuffedShroom', emoji: '', tier: 6, weight: 1, stack: 10,
    Food: { health: 25, stamina: 15, eitr: 75, duration: 1500, regen: 3 } },
  { type: 'item', id: 'SeekerAspic', emoji: '', tier: 6, weight: 1, stack: 10,
    Food: { health: 28, stamina: 14, eitr: 85, duration: 1800, regen: 3 } },
  { type: 'item', id: 'BugMeat', emoji: '🍖', tier: 6, weight: 1, stack: 20 },
  { type: 'item', id: 'CookedBugMeat', emoji: '🍖', tier: 6, weight: 1, stack: 20,
    Food: { health: 60, stamina: 20, duration: 1200, regen: 5 } },
  { type: 'item', id: 'MushroomOmelette', emoji: '🍳', tier: 6, weight: 1, stack: 10,
    Food: { health: 28, stamina: 85, duration: 1500, regen: 3 } },
  { type: 'item', id: 'Salad', emoji: '🥗', tier: 6, weight: 1, stack: 10,
    Food: { health: 26, stamina: 80, duration: 1500, regen: 3 } },
  { type: 'item', id: 'FeastMistlands', emoji: '🥙', tier: 6, weight: 1, stack: 1,
    Food: { health: 65, stamina: 65, eitr: 33, duration: 3000, regen: 5 },
  },
  { type: 'item', id: 'MeadBaseHealthMajor', emoji: '🍲', tier: 6, weight: 1, stack: 1 },
  { type: 'item', id: 'MeadHealthMajor', emoji: '\u{1F9EA}', tier: 6, weight: 1, stack: 10,
    Potion: { health: [125, 10], cooldown: 120 },
  },
  { type: 'item', id: 'MeadBaseEitrMinor', emoji: '🍲', tier: 6, weight: 1, stack: 1 },
  { type: 'item', id: 'MeadEitrMinor', emoji: '\u{1F9EA}', tier: 6, weight: 1, stack: 10,
    Potion: { eitr: [125, 10], cooldown: 120 },
  },
  { type: 'item', id: 'MeadBaseStaminaLingering', emoji: '🍲', tier: 6, weight: 1, stack: 1 },
  { type: 'item', id: 'MeadStaminaLingering', emoji: '\u{1F9EA}', tier: 6, weight: 1, stack: 10,
    Potion: { staminaRegen: 1.25, cooldown: 300 },
  },
  { type: 'item', id: 'Wisp', emoji: '🔵', tier: 6, weight: 0.5, stack: 50 },
  { type: 'item', id: 'Sap', emoji: '🌿', tier: 6, weight: 0.2, stack: 50 },
  { type: 'item', id: 'JuteBlue', emoji: '', tier: 6, weight: 2, stack: 50 },
  { type: 'item', id: 'RoyalJelly', emoji: '', tier: 6, weight: 0.1, stack: 50,
    Food: { health: 15, stamina: 15, duration: 900, regen: 1 } },
  { type: 'item', group: 'lumber', id: 'YggdrasilWood', emoji: '🪵', tier: 6, weight: 2, stack: 50, floating },
  { type: 'item', id: 'YggdrasilPorridge', emoji: '🥣', tier: 6, weight: 1, stack: 10,
    Food: { health: 27, stamina: 13, eitr: 80, duration: 1500, regen: 3 } },
  { type: 'item', id: 'DvergrKeyFragment', emoji: '🗝', tier: 6, weight: 0.5, stack: 9 },
  { type: 'item', id: 'DvergrKey', emoji: '🗝', tier: 6, weight: 0.5, stack: 1 },
  { type: 'item', id: 'GiantBloodSack', emoji: '🩸', tier: 6, weight: 0.5, stack: 50 },
  { type: 'item', id: 'Bilebag', emoji: '👝', tier: 6, weight: 0.5, stack: 50 },
  { type: 'item', id: 'Carapace', emoji: '🐚', tier: 6, weight: 2, stack: 50 },
  { type: 'item', id: 'Mandible', emoji: '', tier: 6, weight: 0.5, stack: 50 },
  { type: 'item', id: 'BlackMarble', emoji: '⚫', tier: 6, weight: 2, stack: 50 },
  { type: 'item', id: 'Softtissue', emoji: '🧠', tier: 6, weight: 1, stack: 40 },
  { type: 'item', id: 'DvergrNeedle', emoji: '📌', tier: 6, teleportable: false, weight: 5, stack: 10 },
  { type: 'item', id: 'MechanicalSpring', emoji: '🪀', tier: 6, teleportable: false, weight: 5, stack: 10 },
  { type: 'item', id: 'Eitr', emoji: '🟢', tier: 6, weight: 5, stack: 30,
    PointLight: { range: 1, intensity: 2, color: '#00FF8F' },
    Radiation: { rate: [5, 20], velocity: 20, damage: dmg({ lightning: 30, poison: 30 }) },
  },
  { type: 'item', id: 'BlackCore', emoji: '🟣', tier: 6, weight: 1, stack: 40 },
  { type: 'item', id: 'QueenDrop', emoji: '💄', tier: 6, weight: 1, stack: 30, floating,
    PointLight: { color: '#13BBFF', range: 3.76, intensity: 1.24 },
  },
  { type: 'trophy', id: 'TrophyHare', trophyPos: { x: 2, y: 5 }, tier: 6, weight: 2, stack: 20 },
  { type: 'trophy', id: 'TrophyTick', trophyPos: { x: 1, y: 5 }, tier: 6, weight: 2, stack: 20 },
  { type: 'trophy', id: 'TrophySeeker', trophyPos: { x: 0, y: 5 }, tier: 6, weight: 2, stack: 20 },
  { type: 'trophy', id: 'TrophySeekerBrute', trophyPos: { x: 4, y: 5 }, tier: 6, weight: 2, stack: 20, summon: ['SeekerQueen', 3] },
  { type: 'trophy', id: 'TrophyGjall', trophyPos: { x: 5, y: 5 }, tier: 6, weight: 2, stack: 20 },
  { type: 'trophy', id: 'TrophyDvergr', trophyPos: { x: 6, y: 5 }, tier: 6, weight: 2, stack: 20 },
  { type: 'trophy', id: 'TrophySeekerQueen', trophyPos: { x: 3, y: 5 }, tier: 6, weight: 2, stack: 20, floating,
    Deadspeak: deadSpeakBoss('ui.deadspeak.queen'), },
  { type: 'item', id: 'CeramicPlate', tier: 6, weight: 2, stack: 30 },
// ASHLANDS
  { type: 'item', group: 'lumber', id: 'Blackwood', emoji: '🪵', tier: 7, weight: 2, stack: 50, floating },
  { type: 'item', id: 'Grausten', emoji: '🪨', tier: 7, weight: 2, stack: 50 },
  { type: 'item', id: 'BonemawSerpentTooth', tier: 7, weight: 2, stack: 50, floating },
  { type: 'item', id: 'BonemawSerpentScale', tier: 7, weight: 2, stack: 50, disabled },
  { type: 'item', id: 'BoneMawSerpentMeat', tier: 7, weight: 10, stack: 50, floating },
  { type: 'item', id: 'CelestialFeather', tier: 7, weight: 0.3, stack: 50 },
  { type: 'item', id: 'CharcoalResin', tier: 7, weight: 0.3, stack: 50 },
  { type: 'item', id: 'SulfurStone', tier: 7, weight: 2, stack: 50 },
  { type: 'item', id: 'ProustitePowder', tier: 7, weight: 0.5, stack: 50 },
  { type: 'item', id: 'CharredBone', tier: 7, weight: 0.5, stack: 50 },
  { type: 'item', id: 'CharredCogwheel', tier: 7, weight: 5, stack: 10, teleportable: false },
  { type: 'item', id: 'Charredskull', tier: 7, weight: 2, stack: 50 },
  { type: 'item', id: 'MorgenSinew', tier: 7, weight: 0.5, stack: 50 },
  { type: 'item', id: 'MorgenHeart', tier: 7, weight: 0.5, stack: 50 },
  { type: 'item', id: 'Fiddleheadfern', tier: 7, weight: 0.1, stack: 50,
    Food: { health: 30, stamina: 30, duration: 900, regen: 1 }
  },
  { type: 'item', id: 'MushroomSmokePuff', tier: 7, weight: 0.1, stack: 50,
    Food: { health: 15, stamina: 15, duration: 900, regen: 1 },
    grow: itemGrow({ locations: ['Ashlands'], randTilt: 0, altitude: [2, 1000], tilt: [0, 60], num: [2, 2], group: [1, 3], groupRadius: 5 }),
  },
  { type: 'item', id: 'Vineberry', tier: 7, weight: 0.1, stack: 50,
    Food: { health: 30, stamina: 30, duration: 900, regen: 1 }
  },
  { type: 'item', id: 'VineberrySeeds', tier: 7, weight: 0.1, stack: 100 },
  { type: 'item', group: 'ore', id: 'FlametalOre', tier: 6, weight: 12, stack: 30, teleportable: false, disabled },
  { type: 'item', group: 'metal', id: 'Flametal', tier: 6, weight: 12, stack: 30, teleportable: false, disabled },
  { type: 'item', group: 'ore', id: 'FlametalOreNew', tier: 7, weight: 12, stack: 30, teleportable: false },
  { type: 'item', group: 'metal', id: 'FlametalNew', tier: 7, weight: 12, stack: 30, teleportable: false },
  { type: 'item', id: 'ShieldCore', tier: 7, weight: 1, stack: 20 },
  { type: 'item', id: 'MoltenCore', tier: 7, weight: 1, stack: 20 },
  { type: 'item', group: 'gem', id: 'GemstoneRed', tier: 7, weight: 2, stack: 50 },
  { type: 'item', group: 'gem', id: 'GemstoneGreen', tier: 7, weight: 2, stack: 50 },
  { type: 'item', group: 'gem', id: 'GemstoneBlue', tier: 7, weight: 2, stack: 50 },
  { type: 'item', id: 'AskBladder', tier: 7, weight: 0.5, stack: 50 },
  { type: 'item', id: 'AsksvinMeat', tier: 7, weight: 1, stack: 20 },
  { type: 'item', id: 'CookedAsksvinMeat', tier: 7, weight: 1, stack: 20,
    Food: { health: 70, stamina: 24, duration: 1200, regen: 6 }
  },
  { type: 'item', group: 'hide', id: 'AskHide', tier: 7, weight: 1, stack: 50 },
  { type: 'item', id: 'SaddleAsksvin', tier: 7, weight: 10, stack: 1, floating },
  { type: 'item', id: 'AsksvinEgg', tier: 7, weight: 1, stack: 20,
    EggGrow: { growTime: 1800, grownId: 'Asksvin_hatchling', requiresNearbyFire: true, requiresUnderRoof: false },
  },
  { type: 'item', id: 'AsksvinCarrionNeck', tier: 7, weight: 2, stack: 50 },
  { type: 'item', id: 'AsksvinCarrionPelvic', tier: 7, weight: 2, stack: 50 },
  { type: 'item', id: 'AsksvinCarrionRibcage', tier: 7, weight: 5, stack: 50 },
  { type: 'item', id: 'AsksvinCarrionSkull', tier: 7, weight: 2, stack: 50 },
  { type: 'item', id: 'VoltureEgg', tier: 7, weight: 1, stack: 20 },
  { type: 'item', id: 'VoltureMeat', tier: 7, weight: 1, stack: 20, floating },
  { type: 'item', id: 'CookedVoltureMeat', tier: 7, weight: 1, stack: 20,
    Food: { health: 70, stamina: 24, duration: 1200, regen: 6 },
  },
  { type: 'item', id: 'MashedMeat', tier: 7, weight: 1, stack: 10,
    Food: { health: 100, stamina: 34, duration: 1500, regen: 6 },
  },
  { type: 'item', id: 'FierySvinstew', tier: 7, weight: 1, stack: 10,
    Food: { health: 95, stamina: 32, duration: 1500, regen: 6 },
  },
  { type: 'item', id: 'PiquantPieUncooked', tier: 7, weight: 1, stack: 10 },
  { type: 'item', id: 'PiquantPie', tier: 7, weight: 1, stack: 10,
    Food: { health: 105, stamina: 35, duration: 1800, regen: 6 },
  },
  { type: 'item', id: 'CookedBoneMawSerpentMeat', tier: 7, weight: 10, stack: 50, floating,
    Food: { health: 90, stamina: 30, duration: 1500, regen: 6 }
  },

  // TODO: stats
  { type: 'item', id: 'SpicyMarmalade', tier: 7, weight: 1, stack: 10,
    Food: { health: 28, stamina: 14, eitr: 85, duration: 1500, regen: 4 },
  },
  { type: 'item', id: 'ScorchingMedley', tier: 7, weight: 1, stack: 10,
    Food: { health: 28, stamina: 14, eitr: 85, duration: 1500, regen: 4 },
  },
  { type: 'item', id: 'RoastedCrustPieUncooked', tier: 7, weight: 1, stack: 10 },
  { type: 'item', id: 'RoastedCrustPie', tier: 7, weight: 1, stack: 10,
    Food: { health: 105, stamina: 24, duration: 1800, regen: 6 },
  },

  { type: 'item', id: 'SizzlingBerryBroth', tier: 7, weight: 1, stack: 10,
    Food: { health: 28, stamina: 14, eitr: 85, duration: 1500, regen: 4 },
  },
  { type: 'item', id: 'SparklingShroomshake', tier: 7, weight: 1, stack: 10,
    Food: { health: 30, stamina: 15, eitr: 90, duration: 1500, regen: 4 },
  },
  { type: 'item', id: 'MarinatedGreens', tier: 7, weight: 1, stack: 10,
    Food: { health: 32, stamina: 16, eitr: 95, duration: 1800, regen: 4 },
  },
  { type: 'item', id: 'FeastAshlands', emoji: '🥙', tier: 7, weight: 1, stack: 1,
    Food: { health: 75, stamina: 75, eitr: 38, duration: 3000, regen: 6 },
  },
  { type: 'item', id: 'MeadBaseEitrLingering', emoji: '🍲', tier: 7, weight: 1, stack: 1 },
  { type: 'item', id: 'MeadEitrLingering', emoji: '\u{1F9EA}', tier: 7, weight: 1, stack: 10,
    Potion: { eitrRegen: 1.25, cooldown: 300 },
  },
  { type: 'item', id: 'MeadBaseHealthLingering', emoji: '🍲', tier: 7, weight: 1, stack: 1 },
  { type: 'item', id: 'MeadHealthLingering', emoji: '\u{1F9EA}', tier: 7, weight: 1, stack: 10,
    Potion: { healthRegen: 1.25, cooldown: 300 },
  },
  { type: 'item', id: 'Pot_Shard_Green', tier: 7, weight: 2, stack: 50 },
  { type: 'item', id: 'Pot_Shard_Red', tier: 7, disabled, weight: 2, stack: 50 },
  { type: 'item', id: 'DyrnwynHiltFragment', tier: 7, weight: 0.5, stack: 1 },
  { type: 'item', id: 'DyrnwynBladeFragment', tier: 7, weight: 0.5, stack: 1 },
  { type: 'item', id: 'DyrnwynTipFragment', tier: 7, weight: 0.5, stack: 1 },
  { type: 'item', id: 'BellFragment', tier: 7, weight: 0.5, stack: 9, floating },
  { type: 'item', id: 'Bell', tier: 7, weight: 0.5, stack: 9, floating, summon: ['Fader', 3] },
  
  { type: 'trophy', id: 'TrophyBonemawSerpent', trophyPos: { x: 1, y: 8 }, tier: 7, weight: 2, stack: 20 },
  { type: 'trophy', id: 'TrophyCharredArcher', trophyPos: { x: 2, y: 7 }, tier: 7, weight: 2, stack: 20 },
  { type: 'trophy', id: 'TrophyCharredMage', trophyPos: { x: 1, y: 7 }, tier: 7, weight: 2, stack: 20 },
  { type: 'trophy', id: 'TrophyCharredMelee', trophyPos: { x: 0, y: 7 }, tier: 7, weight: 2, stack: 20 },
  { type: 'trophy', id: 'TrophyFallenValkyrie', trophyPos: { x: 4, y: 7 }, tier: 7, weight: 2, stack: 20 },
  { type: 'trophy', id: 'TrophyMorgen', trophyPos: { x: 5, y: 7 }, tier: 7, weight: 2, stack: 20 },
  { type: 'trophy', id: 'TrophyAsksvin', trophyPos: { x: 6, y: 7 }, tier: 7, weight: 2, stack: 20 },
  { type: 'trophy', id: 'TrophyVolture', trophyPos: { x: 0, y: 8 }, tier: 7, weight: 2, stack: 20 },
  { type: 'trophy', id: 'TrophyFader', trophyPos: { x: 3, y: 6 }, tier: 7, weight: 2, stack: 20 },
  { type: 'item', id: 'FaderDrop', tier: 7, weight: 1, stack: 9, floating },
  // QUEST
  { type: 'item', id: 'Ironpit', emoji: '🔥', tier: 2, teleportable: false, weight: 10 },
  { type: 'item', id: 'BarberKit', emoji: '🪒', tier: 2, weight: 2 },
  { type: 'item', id: 'HildirKey_forestcrypt', emoji: '🗝', tier: 2, disabled, weight: 0.1, floating },
  { type: 'item', id: 'HildirKey_mountaincave', emoji: '🗝', tier: 2, disabled, weight: 0.1, floating },
  { type: 'item', id: 'HildirKey_plainsfortress', emoji: '🗝', tier: 2, disabled, weight: 0.1, floating },
  { type: 'item', id: 'FireworksRocket_White', emoji: '🎆', tier: 2, weight: 0.5, stack: 10 },
  { type: 'item', id: 'FireworksRocket_Blue', emoji: '🎆', tier: 2, weight: 0.5, stack: 10 },
  { type: 'item', id: 'FireworksRocket_Cyan', emoji: '🎆', tier: 2, weight: 0.5, stack: 10 },
  { type: 'item', id: 'FireworksRocket_Green', emoji: '🎆', tier: 3, weight: 0.5, stack: 10 },
  { type: 'item', id: 'FireworksRocket_Purple', emoji: '🎆', tier: 3, weight: 0.5, stack: 10 },
  { type: 'item', id: 'FireworksRocket_Red', emoji: '🎆', tier: 2, weight: 0.5, stack: 10 },
  { type: 'item', id: 'FireworksRocket_Yellow', emoji: '🎆', tier: 2, weight: 0.5, stack: 10 },
  { type: 'item', id: 'chest_hildir1', emoji: '🧰', tier: 2, teleportable: false, weight: 200 },
  { type: 'item', id: 'chest_hildir2', emoji: '🧰', tier: 4, teleportable: false, weight: 200 },
  { type: 'item', id: 'chest_hildir3', emoji: '🧰', tier: 5, teleportable: false, weight: 200 },
];
for (const item of resources) {
  item.components = ['ItemDrop', 'ZSyncTransform'];
  if ('grow' in item) {
    item.components.push('Pickable');
  }
}

const summonMap: Partial<Record<EntityId, Resource['summon']>> = {};
resources.forEach(r => {
  if (r.type === 'item' && r.summon) {
    const [id, number] = r.summon;
    summonMap[id] = [r.id, number];
  }
});

export const getSummon = (id: EntityId): Resource['summon'] => {
  return summonMap[id];
}

// VegvisirShard_Bonemass
// HealthUpgrade_Bonemass
// HealthUpgrade_GDKing
// StaminaUpgrade_Greydwarf
// StaminaUpgrade_Troll
// StaminaUpgrade_Wraith
